import axios from "axios";
import Vue from "vue";

export default {
	namespaced: true,
	state: {
		tags: [],
		selfListingsResponse: null,
		merchantListings: [],
		merchantSeats: null,
	},
	mutations: {
		setTags(state, payload) {
			Vue.set(state, "tags", payload);
		},
		setSelfListingsResponse(state, payload) {
			Vue.set(state, "selfListingsResponse", payload);
		},
		setMerchantListings(state, payload) {
			Vue.set(state, "merchantListings", payload);
		},
		setMerchantSeats(state, payload) {
			Vue.set(state, "merchantSeats", payload);
		},
	},
	getters: {
		tags(state) {
			return state.tags;
		},
		getSelfListingsResponse(state) {
			return state.selfListingsResponse;
		},
		getMerchantListings(state) {
			return state.merchantListings;
		},
		getMerchantSeats(state) {
			return state.merchantSeats?.content;
		},
		getActiveSeats(state) {
			return state.merchantSeats?.content.filter((seat) => seat.status === "ACTIVE") || [];
		},
		getSelfListingsMerchant(state) {
			if (state.selfListingsResponse === null) {
				return [];
			}

			return state.selfListingsResponse.results
				.filter((item) => item.listingType === "merchant")
				.sort((a, b) => new Date(a.activeDate) - new Date(b.activeDate));
		},
		getSelfListingById(state) {
			return ({id}) => {
				if (state.selfListingsResponse === null) {
					return null;
				}

				return state.selfListingsResponse.results.find((item) => {
					return item.id === id;
				});
			};
		},
	},
	actions: {
		//For admins only
		async requestAllListingsByType(ctx, {type, start = 0, size = 15}) {
			return axios.get(`/api/v1/${type}/listings`, {
				params: {
					start,
					size,
				},
			});
		},
		// Active = true <---- All active listings
		// Active = false <---- All inactive listings
		// Active = null <----- Any type of listing (for admin). Others have active set to true by default.
		async searchListingsByType(ctx, {query, type, start = 0, size = 15}) {
			// Object.keys(query).forEach((key) => {
			//     if (query[key] == null) delete query[key];
			// });

			return axios.post(`/api/v1/${type}/search`, query, {
				params: {
					start,
					size,
				},
			});
		},
		async genericSearchListings(ctx, {request, start = 0, size = 15}) {
			return axios.post(`/api/v1/listing/search`, request, {
				params: {
					start,
					size,
				},
			});
		},
		//For users
		async requestListingById(_, {type, id}) {
			return axios.get(`/api/v1/${type}/listing/${id}`).then((response) => {
				if (response.status === 200) {
					return response.data;
				} else {
					return null;
				}
			});
		},
		async requestListingTags({commit}) {
			return axios.post("/api/v1/tag/all").then((r) => {
				commit("setTags", r.data);
				return r;
			});
		},
		async requestSelfListingsAll() {
			return this.dispatch("api/listings/requestSelfListings", {
				start: 0,
				size: 9999,
				title: null,
			});
		},
		async requestSelfListings({commit}, {start = 0, size = 15, title = null}) {
			let query = new URLSearchParams({
				start,
				size,
				title,
			}).toString();

			return axios.post(`/api/v1/preview/self?${query}`).then((r) => {
				if (r.data && r.data.totalElements) {
					r.data.results.forEach((listing) => {
						if (listing.primaryImage && listing.primaryImage.mimeType) {
							listing.primaryImage.mime_type = listing.primaryImage.mimeType;
							delete listing.primaryImage.mimeType;
						}
					});
				}
				commit("setSelfListingsResponse", r.data);
			});
		},
		async getListingTypeById(_, {id}) {
			return axios.get(`/api/v1/listing/${id}/type`);
		},
		async requestPreviewListings(ctx, ids) {
			return axios.post("/api/v1/preview/search", {listings: ids});
		},
		/**
		 * Creates a listing based on the given type.
		 * * @param ctx The vuex commit context.
		 * @param type The listing type [property|event|classified|merchant|item]
		 * @param listing The listing DTO.
		 * @returns {Promise<*>}
		 */
		async createListing(ctx, {type, listing}) {
			return axios.post(`/api/v1/${type}/listing`, listing);
		},
		/**
		 * Issues a modification request for the given listing based on type and ID.
		 * @param ctx The vuex commit context.
		 * @param type The listing type [property|event|classified|merchant|item]
		 * @param listing The listing DTO. This is declarative, so the entire listing data structure will be replaced by what was passed in.
		 * @param id {Number} The listing ID to modify.
		 * @returns {Promise<*>}
		 */
		async modifyListing(ctx, {type, listing, id}) {
			return axios.put(`/api/v1/${type}/listing/${id}`, listing);
		},
		async deleteListing(ctx, {type, id}) {
			return axios.delete(`/api/v1/${type}/listing/${id}`);
		},
		async setListingPriority(ctx, {type, id, priority}) {
			return axios.post(`/api/v1/${type}/listing/${id}/priority?priority=${priority}`);
		},

		//Listing history
		async getHistoricListing(ctx, {type, id}) {
			return axios.get(`/api/v1/${type}/listing/${id}/revisions`);
		},
		async getRevisions(ctx, {type, id, revision}) {
			return axios.get(`/api/v1/${type}/listing/${id}/revisions/${revision}`);
		},

		//Merchant seat management
		async seatsAdd(ctx, {id, request}) {
			return axios.post(`/api/v1/merchant/listing/${id}/billboards`, request);
		},
		async seatsRemove(ctx, {id, billboard}) {
			return axios.delete(`/api/v1/merchant/listing/${id}/billboard/${billboard}`);
		},
		async seatsGet(ctx, {id}) {
			return axios.get(`/api/v1/merchant/listing/${id}/billboards`);
		},
		async seatSearch(ctx, { id, request = {} }) {
			return axios
				.post(`/api/v1/merchant/listing/${id}/billboards/search`, request)
				.then((response) => {
					if (response.status === 200) {
						ctx.commit("setMerchantSeats", response.data);
						return response.data;
					}
				})
				.catch(() => {
					ctx.commit("setMerchantSeats", null);
				});
		},

		//Mercahnt search
		async getSelfMerchantListings(ctx, {start = 0, size = 100}) {
			return axios
				.post(`/api/v1/merchant/search/self?start=${start}&size=${size}`)
				.then((res) => {
					if (res.data && res.data.totalElements) {
						let results = res.data.results.sort((a, b) => new Date(a.activeDate) - new Date(b.activeDate));
						ctx.commit("setMerchantListings", results);
					}
				})
				.catch(() => {
					ctx.commit("setMerchantListings", []);
				});
		},
		async requestMerchantById(ctx, {id}) {
			return this.dispatch("api/listings/requestListingById", {
				type: "merchant",
				id: id,
			});
		},
		async requestMerchantsByIds(v, {ids, start = 0, size = 12}) {
			return axios.post(`/api/v1/merchant/search?start=${start}&size=${size}`, {
				ids: ids,
			});
		},
	},
};
